import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "@tanstack/react-query";
import { api, queryString, ApiError } from "@/lib/api";
import { ConfirmButton } from "@/components/ConfirmButton";

interface PurgeResult {
  cutoff: string;
  deleted: number;
  dry_run: boolean;
}

/** A generic "delete everything older than N days" housekeeping control:
 * pick a cutoff, preview the count (dry_run), then a two-click confirm before
 * anything is actually (soft-)deleted. Never deletes on its own — every call
 * is a button press. */
export function PurgeOldPanel({
  endpoint,
  title,
  hint,
  foundLabel,
  deletedLabel,
  allowDeleteAll = false,
  deleteAllLabel,
  deleteAllHint,
}: {
  endpoint: string;
  title: string;
  hint: string;
  /** "Found N older than the cutoff" — shown after the preview call. */
  foundLabel: (n: number) => string;
  /** "Deleted N" — shown after the confirmed purge. */
  deletedLabel: (n: number) => string;
  /** Adds a distinct "delete everything now" action — same endpoint with
   * older_than_days=0 (the cutoff is "now", matching every row that already
   * exists), still preview-then-confirm. */
  allowDeleteAll?: boolean;
  deleteAllLabel?: string;
  deleteAllHint?: string;
}) {
  const { t } = useTranslation();
  const [days, setDays] = useState(90);
  const [preview, setPreview] = useState<PurgeResult | null>(null);
  const [done, setDone] = useState<PurgeResult | null>(null);
  const [allPreview, setAllPreview] = useState<PurgeResult | null>(null);
  const [allDone, setAllDone] = useState<PurgeResult | null>(null);

  const previewMut = useMutation({
    mutationFn: () =>
      api.post<PurgeResult>(
        `${endpoint}${queryString({ older_than_days: days, dry_run: "true" })}`,
      ),
    onSuccess: (r) => {
      setPreview(r);
      setDone(null);
    },
  });

  const purgeMut = useMutation({
    mutationFn: () =>
      api.post<PurgeResult>(
        `${endpoint}${queryString({ older_than_days: days, dry_run: "false" })}`,
      ),
    onSuccess: (r) => {
      setDone(r);
      setPreview(null);
    },
  });

  const previewAllMut = useMutation({
    mutationFn: () =>
      api.post<PurgeResult>(
        `${endpoint}${queryString({ older_than_days: 0, dry_run: "true" })}`,
      ),
    onSuccess: (r) => {
      setAllPreview(r);
      setAllDone(null);
    },
  });

  const purgeAllMut = useMutation({
    mutationFn: () =>
      api.post<PurgeResult>(
        `${endpoint}${queryString({ older_than_days: 0, dry_run: "false" })}`,
      ),
    onSuccess: (r) => {
      setAllDone(r);
      setAllPreview(null);
    },
  });

  const busy =
    previewMut.isPending ||
    purgeMut.isPending ||
    previewAllMut.isPending ||
    purgeAllMut.isPending;
  const error = (previewMut.error ?? purgeMut.error ?? previewAllMut.error ?? purgeAllMut.error) as
    | ApiError
    | null;

  return (
    <div className="nm-card p-3 text-sm">
      <div className="font-semibold text-sand">{title}</div>
      <p className="mt-0.5 text-xs text-sand-muted">{hint}</p>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <span className="text-xs text-sand-muted">{t("housekeeping.olderThan")}</span>
        <input
          type="number"
          min={0}
          max={3650}
          value={days}
          onChange={(e) => {
            setDays(Math.max(0, Number(e.target.value) || 0));
            setPreview(null);
            setDone(null);
          }}
          className="w-20 rounded-md border border-olive-600 bg-olive-900 px-2 py-1 text-xs"
        />
        <span className="text-xs text-sand-muted">{t("housekeeping.days")}</span>

        {!preview && (
          <button
            type="button"
            onClick={() => previewMut.mutate()}
            disabled={busy}
            className="rounded-md border border-olive-600 px-2.5 py-1 text-xs text-sand-muted hover:text-sand disabled:opacity-50"
          >
            {previewMut.isPending ? t("common.loading", "…") : t("housekeeping.preview")}
          </button>
        )}

        {preview && (
          <>
            <span className="text-xs text-sand">
              {foundLabel(preview.deleted)}
            </span>
            {preview.deleted > 0 ? (
              <ConfirmButton
                label={t("housekeeping.deleteNow")}
                confirmLabel={t("housekeeping.confirmDelete", { count: preview.deleted })}
                busy={busy}
                onConfirm={() => purgeMut.mutate()}
              />
            ) : null}
            <button
              type="button"
              onClick={() => setPreview(null)}
              className="text-xs text-sand-muted hover:text-sand"
            >
              {t("common.cancel")}
            </button>
          </>
        )}
      </div>

      {done && (
        <p className="mt-2 text-xs text-emerald-700">{deletedLabel(done.deleted)}</p>
      )}

      {allowDeleteAll && (
        <div className="mt-3 border-t border-olive-600/60 pt-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-semibold text-red-700">
              {deleteAllLabel ?? t("housekeeping.deleteAll")}
            </span>
            {deleteAllHint && (
              <span className="text-xs text-sand-muted">{deleteAllHint}</span>
            )}
          </div>
          <div className="mt-1.5 flex flex-wrap items-center gap-2">
            {!allPreview && (
              <button
                type="button"
                onClick={() => previewAllMut.mutate()}
                disabled={busy}
                className="rounded-md bg-[#FEECEC] px-2.5 py-1 text-xs font-medium text-[#B42318] hover:bg-[#FDDCDC] disabled:opacity-50"
              >
                {previewAllMut.isPending
                  ? t("common.loading", "…")
                  : deleteAllLabel ?? t("housekeeping.deleteAll")}
              </button>
            )}
            {allPreview && (
              <>
                <span className="text-xs text-sand">{foundLabel(allPreview.deleted)}</span>
                {allPreview.deleted > 0 ? (
                  <ConfirmButton
                    label={t("housekeeping.deleteNow")}
                    confirmLabel={t("housekeeping.confirmDelete", { count: allPreview.deleted })}
                    busy={busy}
                    onConfirm={() => purgeAllMut.mutate()}
                  />
                ) : null}
                <button
                  type="button"
                  onClick={() => setAllPreview(null)}
                  className="text-xs text-sand-muted hover:text-sand"
                >
                  {t("common.cancel")}
                </button>
              </>
            )}
          </div>
          {allDone && (
            <p className="mt-2 text-xs text-emerald-700">{deletedLabel(allDone.deleted)}</p>
          )}
        </div>
      )}

      {error && (
        <p className="mt-2 text-xs text-red-700">
          {error.message ?? t("common.error", "Error")}
        </p>
      )}
    </div>
  );
}
